import ListCard from '@Components/ListCard/ListCard';
import AssetAssignModal from '@Components/Modals/AssetAssign/AssetAssignModal';
import AssetDocumentsModal from '@Components/Modals/AssetDocuments/AssetDocumentsModal';
import AssetInputModal from '@Components/Modals/AssetInput/AssetInputModal';
import AssetUnassignModal from '@Components/Modals/AssetUnassign/UnassignModal';
import usePagination from '@Hooks/usePagination';
import AssetServices from '@Services/Asset.services.ts';
import { Asset, AssetChange, Assignment, ElementToDelete } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import {
  ADMIN_ROLES,
  ASSET_MODAL_TYPE,
  ELEMENT_TO_DELETE_TYPE,
  PAGE_SIZE,
  PAGINATED_DEFAULT,
  PATHS,
  PERSISTENT_FILTER_PROP,
  TAB_VALUES,
  TABLE_STRUCTURES,
} from '@Utils/constants';
import { formatEditValues } from '@Utils/form.utils';
import {
  formatAssetChangeData,
  formatAssetDetails,
  formatAssetTagInAsset,
  formatAssignmentsData,
  sortDescendingArrayByDate,
} from '@Utils/format.utils.tsx';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, NotFound, Pagination, SectionCard, Toast, useTopHat, useUser } from '@kg-internal/pv3-uikit';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styles from './AssetDetails.module.scss';

const AssetDetails = () => {
  const [assetDetails, setAssetDetails] = useState<Asset>();
  const [currentPageAssignments, setCurrentPageAssignments] = useState<number>(1);
  const [currentPageHistory, setCurrentPageHistory] = useState<number>(1);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [showEditAssetModal, setShowEditAssetModal] = useState<boolean>(false);
  const { id } = useParams();
  const [showDocumentsAssetModal, setShowDocumentsAssetModal] = useState<boolean>(false);
  const [showAssignAssetModal, setShowAssignAssetModal] = useState<boolean>(false);
  const [showUnassignAssetModal, setShowUnassignAssetModal] = useState<boolean>(false);
  const [isAssetAssigned, setIsAssetAssigned] = useState<boolean>(false);
  const [elementToDelete, setElementToDelete] = useState<ElementToDelete>();
  const block = 'asset-details';
  const user = useUser();
  const isAdmin = user.hasPermissions(ADMIN_ROLES);

  const [searchParams] = useSearchParams();
  const getAssetHref = () => {
    const data = searchParams.get(PERSISTENT_FILTER_PROP);
    if (!data) {
      return `${PATHS.ASSETS}?tab=${TAB_VALUES.ASSETS}&page=${1}`;
    }
    return `${PATHS.ASSETS}${data}`;
  };

  const { setTopHat } = useTopHat({
    activeNav: 'nav-assets',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      {
        title: 'Assets',
        href: getAssetHref(),
        id: 'Assets',
      },
      {
        title: id || 'Assets',
        href: `.`,
        id: id || 'Assets',
      },
    ],
  });

  const [assignments, setAssignments] = useState<PaginatedResponse<Assignment>>({
    ...PAGINATED_DEFAULT,
  });

  const [changes, setChanges] = useState<PaginatedResponse<AssetChange>>({
    ...PAGINATED_DEFAULT,
  });

  const { currentData: assignmentsData, jump: assignmentsJump } = usePagination(
    assignments.docs || [],
    PAGE_SIZE,
    undefined,
  );

  const { currentData: historyData, jump: historyJump } = usePagination(changes.docs || [], PAGE_SIZE, undefined);

  // First it checks if there is owner history, if there is, it checks whether the last assigment is undefined (unassigned)
  const checkIfAssetIsAssigned = (asset: Asset) => {
    if (asset) {
      const isAssigned =
        asset.assignments.length > 0 && asset.assignments[asset.assignments.length - 1].employeeEmail !== undefined;
      setIsAssetAssigned(isAssigned);
    }
  };

  const getAsset = () => {
    setIsLoading(true);
    AssetServices.getAsset(id)
      .then(({ data }) => {
        if (!data.length) {
          setErrorMessage('Not found');
          return;
        }
        const asset = data[0];
        setAssetDetails(asset);
        checkIfAssetIsAssigned(asset);
        setAssignments((prev) => ({
          ...prev,
          docs: sortDescendingArrayByDate(asset.assignments, 'assignmentDate'),
        }));
        setChanges((prev) => ({
          ...prev,
          docs: sortDescendingArrayByDate(asset.assetChanges, 'createdAt'),
        }));
        setErrorMessage(undefined);

        // First it checks if there is owner history, if there is, it checks whether the last assigment is undefined (unassigned)
        const isAssigned =
          asset.assignments.length > 0 && asset.assignments[asset.assignments.length - 1].employeeEmail !== undefined;
        setIsAssetAssigned(isAssigned);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setTopHat({
      breadcrumbs: [
        {
          title: 'Assets',
          href: getAssetHref(),
          id: 'Assets',
        },
        {
          title: id || 'Assets',
          href: `.`,
          id: id || 'Assets',
        },
      ],
    });
    getAsset();
  }, [id]);

  const handleUnassignAsset = () => {
    setShowUnassignAssetModal(true);
    setElementToDelete({ id: assetDetails?.id || '', type: ELEMENT_TO_DELETE_TYPE.ASSET });
  };

  const renderOwnerButton = () =>
    isAssetAssigned && !isLoading ? (
      <Button type='secondary' className='button-with-icon' fullWidth onClick={handleUnassignAsset}>
        <i className='fa-solid fa-user-minus' /> Unassign from User
      </Button>
    ) : (
      <Button type='secondary' className='button-with-icon' fullWidth onClick={() => setShowAssignAssetModal(true)}>
        <i className='fa-solid fa-user-plus' /> Assign to User
      </Button>
    );
  const handleEditAssetModalClose = () => {
    setShowEditAssetModal(false);
  };

  const editSubmitHandler = (assetFormData: FormData) => AssetServices.editAsset(id, assetFormData);

  const onEditSuccess = () => {
    Toast.success('Asset successfully updated');
    getAsset();
  };

  const handleDocumentsAssetModalClose = () => {
    setShowDocumentsAssetModal(false);
  };

  const handleAssignAssetModalClose = () => {
    setShowAssignAssetModal(false);
  };

  return (
    <div className='grid'>
      <div className={cx('col-12', { 'col-md-3': isAdmin })}>
        <SectionCard>
          {!assetDetails && !isLoading ? (
            <NotFound />
          ) : (
            <>
              <h2 className={cx({ [styles[`${block}__title--regular`]]: !isAdmin })}>Asset Info</h2>
              {isAdmin && (
                <>
                  {renderOwnerButton()}
                  <br />
                  <Button
                    type='primary'
                    className='button-with-icon'
                    fullWidth
                    onClick={() => setShowEditAssetModal(true)}>
                    {Icons.Edit} Edit
                  </Button>
                  <br />
                  <Button
                    type='secondary'
                    fullWidth
                    onClick={() => setShowDocumentsAssetModal(true)}
                    className='button-with-icon'>
                    <i className='fa-regular fa-eye' />
                    Documents
                  </Button>
                </>
              )}

              {assetDetails && (
                <div
                  className={cx({
                    [styles[`${block}__details-container--regular`]]: !isAdmin,
                  })}>
                  {formatAssetDetails(styles, formatAssetTagInAsset(assetDetails))}
                </div>
              )}
            </>
          )}
        </SectionCard>
      </div>
      {isAdmin && (
        <div className='col-12 col-md-9'>
          <ListCard<Assignment>
            title='Owner History'
            structure={TABLE_STRUCTURES.OWNERS}
            data={formatAssignmentsData(assignmentsData)}
            loading={isLoading}
            detailsPath={PATHS.ASSETS}
            error={errorMessage}
          />
          <div className={`col-12 content-middle content-right-md ${styles[`${block}__pagination`]}`}>
            <Pagination
              count={assignments.docs.length}
              perPage={PAGE_SIZE}
              currentPage={currentPageAssignments - 1}
              onChange={(page) => {
                setCurrentPageAssignments(page + 1);
                assignmentsJump(page + 1);
              }}
            />
          </div>
          <ListCard<AssetChange>
            title='Change History'
            structure={TABLE_STRUCTURES.CHANGE_HISTORY}
            data={formatAssetChangeData(historyData)}
            loading={isLoading}
            detailsPath={PATHS.ASSETS}
            error={errorMessage}
          />
          <div className={`col-12 content-middle content-right-md ${styles[`${block}__pagination`]}`}>
            <Pagination
              count={changes.docs.length}
              perPage={PAGE_SIZE}
              currentPage={currentPageHistory - 1}
              onChange={(page) => {
                setCurrentPageHistory(page + 1);
                historyJump(page + 1);
              }}
            />
          </div>
          <AssetDocumentsModal
            show={showDocumentsAssetModal}
            onClose={handleDocumentsAssetModalClose}
            documents={assetDetails?.documents}
          />
          <AssetAssignModal
            showModal={showAssignAssetModal}
            onClose={handleAssignAssetModalClose}
            onSuccess={() => getAsset()}
            assetId={assetDetails?.id}
          />
          <AssetInputModal
            show={showEditAssetModal}
            onClose={handleEditAssetModalClose}
            onSuccess={onEditSuccess}
            asset={assetDetails}
            title='Edit Asset'
            onSubmit={editSubmitHandler}
            formatter={formatEditValues}
            type={ASSET_MODAL_TYPE.ASSET}
          />
          <AssetUnassignModal
            employeeEmail={assetDetails?.owner}
            showModal={showUnassignAssetModal}
            title='Remove Owner'
            description='Are you sure you want to unassign this asset from the user?'
            onClose={() => setShowUnassignAssetModal(false)}
            onSuccess={() => getAsset()}
            elementsToDelete={elementToDelete ? [elementToDelete] : []}
          />
        </div>
      )}
    </div>
  );
};

export default AssetDetails;
